// Bank › New › Payment received / Payment made: connect money in the bank to
// the invoice(s) it pays. Pick the company, type the amount (and account /
// date) as it shows on the bank statement, then pick the invoice it pays.
// That opens the normal Record Payment form already filled in, so the save
// is the same one Sales / Receivable / Payable use (bank row, balance, due
// date, books). When one amount pays several invoices, what's left stays
// here to place on the next one.

import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { Button, Modal, FormField, EntityCombobox } from './ui';
import RecordSalePaymentModal from './RecordSalePaymentModal';
import RecordPaymentModal from './RecordPaymentModal';
import type { BankAccount, Entity, PurchaseInvoice, SaleInvoice } from '../types';

export type BankPaymentKind = 'received' | 'made';

type Inv = (SaleInvoice | PurchaseInvoice) & { _date: string; _owed: number };

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(s?: string) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const round2 = (n: number) => Math.round(n * 100) / 100;

// Money in (arrow down into a tray) / money out (arrow up out of it).
function IconMoneyIn() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M7 1.5v7M4 5.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.8 9.5v1.8a1 1 0 001 1h8.4a1 1 0 001-1V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}
function IconMoneyOut() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M7 8.5v-7M4 4.5l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.8 9.5v1.8a1 1 0 001 1h8.4a1 1 0 001-1V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}

export default function BankPaymentPicker({ kind, accounts, defaultAccountId, onClose }: {
  kind: BankPaymentKind;
  accounts: BankAccount[];
  /** The account the Bank page is showing, if one is picked. */
  defaultAccountId?: string;
  onClose: () => void;
}) {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const sale = kind === 'received';

  const [entities, setEntities] = useState<Entity[]>([]);
  const [entityId, setEntityId] = useState('');
  const [amount, setAmount] = useState('');
  const [paidDate, setPaidDate] = useState(todayISO());
  const [accountId, setAccountId] = useState(
    accounts.find(a => a.id === defaultAccountId)?.id ?? accounts.find(a => a.isDefault)?.id ?? accounts[0]?.id ?? '',
  );
  const [invoices, setInvoices] = useState<Inv[]>([]);
  const [placed, setPlaced] = useState(0);
  const [paying, setPaying] = useState<Inv | null>(null);
  const [done, setDone] = useState<{ no: string; amount: number }[]>([]);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    return onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
  }, [business]);

  // The company's open invoices, live (a payment saved here drops off / shrinks at once).
  useEffect(() => {
    const db = getClientDb();
    if (!db || !entityId) return;
    return onSnapshot(
      query(collection(db, sale ? 'sales' : 'invoices'), where(sale ? 'toEntityId' : 'fromEntityId', '==', entityId)),
      snap => {
        const rows = snap.docs.map(d => {
          const inv = { id: d.id, ...d.data() } as SaleInvoice & PurchaseInvoice;
          const owed = Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));
          return { ...inv, _date: sale ? inv.saleDate : inv.purchaseDate, _owed: round2(owed) } as Inv;
        })
          .filter(i => i.paymentStatus !== 'paid' && i._owed > 0.005)
          .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
        setInvoices(rows);
      },
      () => {},
    );
  }, [entityId, sale]);

  const parties = useMemo(
    () => entities.filter(e => e.types.includes(sale ? 'customer' : 'supplier'))
      .sort((a, b) => a.companyName.localeCompare(b.companyName)),
    [entities, sale],
  );
  const partyName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '';
  const total = parseFloat(amount) || 0;
  const left = round2(Math.max(0, total - placed));
  const owedAll = round2(invoices.reduce((s, i) => s + i._owed, 0));
  const today = todayISO();

  // What goes on the next invoice: what's left of the bank amount, capped
  // at what that invoice still owes (or the whole owed when no amount yet).
  const suggestFor = (inv: Inv) => (total > 0 ? Math.min(left, inv._owed) : inv._owed);

  return (
    <>
      <Modal
        open onClose={onClose} size="2xl"
        title={sale ? 'Payment Received' : 'Payment Made'}
        subtitle={sale ? 'Money in from a customer, put against their invoices.' : 'Money out to a supplier, put against their invoices.'}
        footer={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
            {done.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600, marginRight: 'auto' }}>
                Saved: {done.map(d => `${shortInvoiceNo(d.no)} ${money(d.amount)}`).join(' · ')}
              </span>
            )}
            <Button variant="secondary" onClick={onClose} style={{ marginLeft: done.length ? undefined : 'auto' }}>
              {done.length ? 'Done' : 'Cancel'}
            </Button>
          </div>
        }
      >
        <div className="bpp-head">
          <FormField label={sale ? 'Customer' : 'Supplier'} required>
            <EntityCombobox entities={parties} value={entityId}
              onChange={id => { setEntityId(id); setInvoices([]); setPlaced(0); setDone([]); }}
              placeholder={sale ? 'Type to find the customer…' : 'Type to find the supplier…'} />
          </FormField>
          <FormField label="Amount (as on the bank)">
            <input type="number" min="0" step="0.01" className="no-spinner" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="Optional" />
          </FormField>
          <FormField label={sale ? 'Received Into' : 'Paid From'}>
            <select value={accountId} onChange={e => setAccountId(e.target.value)}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
            </select>
          </FormField>
          <FormField label="Date">
            <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
          </FormField>
        </div>

        {total > 0 && (
          <div className="bpp-left">
            <span>Amount <strong>{money(total)}</strong></span>
            <span>Placed <strong style={{ color: 'var(--success)' }}>{money(placed)}</strong></span>
            <span>Left to place <strong style={{ color: left > 0.005 ? 'var(--danger)' : 'var(--success)' }}>{money(left)}</strong></span>
          </div>
        )}

        {/* Invoice list: header and Owed total stay put, only the rows scroll. */}
        {!entityId ? (
          <div className="sm-empty">Pick the {sale ? 'customer' : 'supplier'} first. Their open invoices show here.</div>
        ) : invoices.length === 0 ? (
          <div className="sm-empty">{partyName(entityId)} has no open invoices.</div>
        ) : (
          <div className="table-wrap table-fit" style={{ maxHeight: 420 }}>
            <table>
              <thead><tr>
                <th style={{ width: 40, textAlign: 'right' }}>#</th>
                <th>Invoice No</th><th>Date</th><th>Due</th>
                <th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Owed</th><th style={{ width: 140 }} />
              </tr></thead>
              <tbody>
                {invoices.map((inv, i) => {
                  const late = inv.dueDate && inv.dueDate < today;
                  const put = suggestFor(inv);
                  return (
                    <tr key={inv.id}>
                      <td style={{ textAlign: 'right', color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>{i + 1}</td>
                      <td><span className="sm-ref" title={inv.invoiceNo}>{shortInvoiceNo(inv.invoiceNo)}</span></td>
                      <td style={{ fontFamily: 'var(--font-num)' }}>{fmtDay(inv._date)}</td>
                      <td style={{ fontFamily: 'var(--font-num)', color: late ? 'var(--danger)' : undefined }}>{fmtDay(inv.dueDate)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(inv.totalAmountUSD)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(inv._owed)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button variant="primary" size="sm" disabled={total > 0 && left <= 0.005} onClick={() => setPaying(inv)}
                          icon={total > 0 && left <= 0.005 ? undefined : sale ? <IconMoneyIn /> : <IconMoneyOut />}
                          title={total > 0 && left <= 0.005 ? undefined : `Opens the payment form with ${money(put)} filled in`}>
                          {total > 0 && left <= 0.005 ? 'All placed' : sale ? 'Record' : 'Pay'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ fontWeight: 700 }}>Owed in all · {invoices.length} invoice{invoices.length === 1 ? '' : 's'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(owedAll)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Modal>

      {sale ? (
        <RecordSalePaymentModal
          invoice={paying as SaleInvoice | null}
          entityName={paying ? partyName(entityId) : undefined}
          prefill={paying ? { amount: suggestFor(paying), bankAccountId: accountId, paidDate } : undefined}
          onSaved={amt => { setPlaced(p => round2(p + amt)); setDone(d => [...d, { no: paying?.invoiceNo ?? '', amount: amt }]); }}
          onClose={() => setPaying(null)}
        />
      ) : (
        <RecordPaymentModal
          invoice={paying as PurchaseInvoice | null}
          entityName={paying ? partyName(entityId) : undefined}
          prefill={paying ? { amount: suggestFor(paying), bankAccountId: accountId, paidDate } : undefined}
          onSaved={amt => { setPlaced(p => round2(p + amt)); setDone(d => [...d, { no: paying?.invoiceNo ?? '', amount: amt }]); }}
          onClose={() => setPaying(null)}
        />
      )}
    </>
  );
}
